import DOMPurify from 'dompurify';
import ReactMarkdown from 'react-markdown';
import { Card } from '@/components/ui/card';
import { FileText, Download, Target } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import type { CourseLesson } from '@/types/courses';

interface PlayerLessonContentProps {
  lesson: CourseLesson;
}

export function PlayerLessonContent({ lesson }: PlayerLessonContentProps) {
  return (
    <>
      {/* Attachments */}
      {lesson.attachments && lesson.attachments.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-3">Lesson Materials</h3>
          <div className="space-y-2">
            {lesson.attachments.map((attachment, index) => (
              <a
                key={index}
                href={attachment.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <FileText className="h-5 w-5 text-gray-500" />
                  <div>
                    <p className="font-medium">{attachment.name}</p>
                    <p className="text-sm text-gray-500">
                      {(attachment.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>
                <Download className="h-5 w-5 text-blue-500" />
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Why This Matters */}
      <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-800/30 rounded-lg p-3 flex gap-3 items-start">
        <Target className="w-5 h-5 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
        <div>
          <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-300">
            Why this matters for the Bar
          </h4>
          <p className="text-xs text-blue-800 dark:text-blue-400/80 mt-0.5">
            This topic frequently appears in{' '}
            <span className="font-medium">Integrated Question Sets</span>.
            Mastery here supports the "Issue Spotting" skill.
          </p>
        </div>
      </div>

      {/* Lesson Content */}
      {lesson.content && lesson.content.trim() && (
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-100">
            Lesson Content
          </h3>
          <div className="prose dark:prose-invert max-w-none">
            {lesson.content.includes('<') && lesson.content.includes('>') ? (
              <div
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(lesson.content),
                }}
              />
            ) : lesson.content.includes('#') ||
              lesson.content.includes('**') ||
              lesson.content.includes('[') ? (
              <ReactMarkdown>{lesson.content}</ReactMarkdown>
            ) : (
              <div style={{ whiteSpace: 'pre-wrap' }}>{lesson.content}</div>
            )}
          </div>
        </Card>
      )}

      {/* Notes & Outline Tabs */}
      <div className="mb-8">
        <Tabs defaultValue="notes" className="flex flex-col">
          <div className="border-b border-gray-200 dark:border-gray-800">
            <TabsList className="bg-transparent p-0 gap-6 h-auto">
              {['Notes', 'Outline'].map((tab) => (
                <TabsTrigger
                  key={tab}
                  value={tab.toLowerCase()}
                  className="rounded-none border-b-2 border-transparent px-0 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 data-[state=active]:border-[#F37022] data-[state=active]:text-[#F37022] data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=inactive]:bg-transparent shadow-none"
                >
                  {tab}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
          <TabsContent value="notes" className="mt-4">
            <div className="bg-[#FFFFF0] dark:bg-yellow-900/10 border border-yellow-100 dark:border-yellow-900/20 rounded-lg p-4 h-48 shadow-sm">
              <textarea
                className="w-full h-full bg-transparent resize-none focus:outline-none text-gray-700 dark:text-gray-300 text-sm leading-relaxed"
                placeholder="Take notes on this lesson..."
              />
            </div>
          </TabsContent>
          <TabsContent value="outline" className="mt-4">
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 h-48 overflow-auto flex items-center justify-center text-center">
              <div>
                <FileText className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500">Mini-outline for this lesson.</p>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}
